import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaEye, FaStar, FaHeart, FaRegHeart, FaPhone, FaEnvelope, FaMapMarkerAlt, FaUser } from 'react-icons/fa';
import './Products.css';

const Products = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [wishlistItems, setWishlistItems] = useState(new Set());
  const [sellerAddresses, setSellerAddresses] = useState([]);
  const [showInterestedBuyersModal, setShowInterestedBuyersModal] = useState(false);
  const [selectedProductInterests, setSelectedProductInterests] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    search: searchParams.get('search') || '',
    type: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Electronics',
    type: 'product',
    pricing: {
      basePrice: '',
      gst: 18,
      moq: 1,
      unit: 'piece'
    },
    inventory: {
      quantity: 0,
      available: true
    },
    shippingAddressId: ''
  });

  useEffect(() => {
    fetchProducts();
    if (user?.userType === 'buyer') {
      fetchWishlist();
    }
    if (user?.userType === 'seller') {
      fetchSellerAddresses();
    }
  }, [filters]);

  const fetchSellerAddresses = async () => {
    try {
      const response = await api.get('/address-book');
      if (response.data.success) {
        const shippingAddresses = response.data.addresses.filter(
          addr => addr.addressType === 'shop' || addr.addressType === 'warehouse' || addr.addressType === 'office'
        );
        setSellerAddresses(shippingAddresses);
      }
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const isSeller = user?.userType === 'seller';
      const url = isSeller ? '/products/my-products' : '/products';
      const params = isSeller ? '' : new URLSearchParams(filters).toString();
      const response = await api.get(params ? `${url}?${params}` : url);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchWishlist = async () => {
    try {
      const response = await api.get('/wishlist');
      const wishlistIds = new Set(response.data.wishlist?.map(item => item._id) || []);
      setWishlistItems(wishlistIds);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    }
  };

  const toggleWishlist = async (productId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      if (wishlistItems.has(productId)) {
        await api.delete(`/wishlist/${productId}`);
        setWishlistItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
        toast.success('Removed from wishlist');
      } else {
        await api.post(`/wishlist/${productId}`);
        setWishlistItems(prev => new Set([...prev, productId]));
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update wishlist');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct._id}`, formData);
        toast.success('Product updated successfully!');
      } else {
        await api.post('/products', formData);
        toast.success('Product created successfully!');
      }
      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description,
      category: product.category,
      type: product.type,
      pricing: product.pricing,
      inventory: product.inventory,
      shippingAddressId: product.shippingAddress || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await api.delete(`/products/${id}`);
        toast.success('Product deleted successfully!');
        fetchProducts();
      } catch (error) {
        toast.error('Failed to delete product');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'Electronics',
      type: 'product',
      pricing: {
        basePrice: '',
        gst: 18,
        moq: 1,
        unit: 'piece'
      },
      inventory: {
        quantity: 0,
        available: true
      }
    });
  };

  const handleMarkInterest = async (productId) => {
    try {
      await api.post(`/products/${productId}/interest`);
      toast.success('Interest registered! Seller will be notified.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to register interest');
    }
  };

  const handleShowInterestedBuyers = async (product) => {
    try {
      const response = await api.get(`/products/${product._id}/interests`);
      setSelectedProductInterests({
        product: product,
        interests: response.data.interests || []
      });
      setShowInterestedBuyersModal(true);
    } catch (error) {
      toast.error('Failed to fetch interested buyers');
    }
  };

  if (loading) return <Layout><div className="loading">Loading products...</div></Layout>;

  const isSeller = user?.userType === 'seller';

  return (
    <Layout>
      <div className="products-page">
        <div className="products-header">
          <div>
            <h1>{isSeller ? 'My Catalog' : 'Products'}</h1>
            <p>{isSeller ? 'Manage your product listings' : 'Browse available products'}</p>
          </div>
          {isSeller && (
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <FaPlus /> Add Product
            </button>
          )}
        </div>

        <div className="filters-bar card">
          <input
            type="text"
            placeholder="Search products..."
            className="form-control"
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          />
          <select
            className="form-control"
            value={filters.category}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
          >
            <option value="">All Categories</option>
            <option value="Electronics">Electronics</option>
            <option value="Textiles">Textiles</option>
            <option value="Machinery">Machinery</option>
            <option value="Agriculture">Agriculture</option>
            <option value="Chemicals">Chemicals</option>
            <option value="Construction">Construction</option>
            <option value="Food & Beverage">Food & Beverage</option>
          </select>
          <select
            className="form-control"
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
          >
            <option value="">All Types</option>
            <option value="product">Products</option>
            <option value="service">Services</option>
          </select>
        </div>

        {products.length === 0 ? (
          <div className="empty-state card">
            <p>No products found</p>
            {isSeller && (
              <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                Add Your First Product
              </button>
            )}
          </div>
        ) : (
          <div className="products-grid">
            {products.map(product => (
              <div key={product._id} className="product-card card">
                <div className="product-image">
                  <img 
                    src={product.images?.[0]?.url || '/assets/no-image.svg'} 
                    alt={product.title}
                    onError={(e) => {
                      e.target.src = '/assets/no-image.svg';
                    }}
                  />
                  {product.verified && (
                    <span className="badge badge-success verified-badge">Verified</span>
                  )}
                  {!isSeller && user?.userType === 'buyer' && (
                    <button
                      className="wishlist-btn"
                      onClick={(e) => toggleWishlist(product._id, e)}
                      title={wishlistItems.has(product._id) ? 'Remove from wishlist' : 'Add to wishlist'}
                    >
                      {wishlistItems.has(product._id) ? (
                        <FaHeart style={{ color: '#ef4444' }} />
                      ) : (
                        <FaRegHeart />
                      )}
                    </button>
                  )}
                </div>
                <div className="product-content">
                  <h3>{product.title}</h3>
                  <p className="product-description">{product.description}</p>
                  <div className="product-meta">
                    <span className="badge badge-info">{product.category}</span>
                    <span className="badge badge-warning">{product.type}</span>
                  </div>
                  <div className="product-pricing">
                    <div className="price">₹{product.pricing.basePrice.toLocaleString('en-IN')}</div>
                    <div className="price-details">
                      <small>+{product.pricing.gst}% GST</small>
                      <small>MOQ: {product.pricing.moq} {product.pricing.unit}</small>
                    </div>
                  </div>
                  {product.ratings?.count > 0 && (
                    <div className="product-rating">
                      <FaStar style={{ color: '#f59e0b' }} />
                      <span>{product.ratings.average.toFixed(1)} ({product.ratings.count})</span>
                    </div>
                  )}
                  {isSeller && product.interests && product.interests.length > 0 && (
                    <div className="product-interests">
                      <span 
                        className="badge badge-success clickable-badge" 
                        onClick={() => handleShowInterestedBuyers(product)}
                        style={{ cursor: 'pointer' }}
                      >
                        👥 {product.interests.length} Interested Buyer{product.interests.length > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                  <div className="product-actions">
                    {isSeller ? (
                      <>
                        <button className="btn btn-outline" onClick={() => handleEdit(product)}>
                          <FaEdit /> Edit
                        </button>
                        <button className="btn btn-outline" onClick={() => handleDelete(product._id)}>
                          <FaTrash /> Delete
                        </button>
                      </>
                    ) : (
                      <>
                        <Link to={`/products/${product._id}`} className="btn btn-primary">
                          <FaEye /> Details
                        </Link>
                        <button className="btn btn-secondary" onClick={() => handleMarkInterest(product._id)}>
                          <FaHeart /> Interest
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Product Title *</label>
                  <input
                    type="text"
                    name="title"
                    className="form-control"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    name="description"
                    className="form-control"
                    rows="3"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Category *</label>
                    <select name="category" className="form-control" value={formData.category} onChange={handleInputChange}>
                      <option value="Electronics">Electronics</option>
                      <option value="Textiles">Textiles</option>
                      <option value="Machinery">Machinery</option>
                      <option value="Agriculture">Agriculture</option>
                      <option value="Chemicals">Chemicals</option>
                      <option value="Construction">Construction</option>
                      <option value="Food & Beverage">Food & Beverage</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Type *</label>
                    <select name="type" className="form-control" value={formData.type} onChange={handleInputChange}>
                      <option value="product">Product</option>
                      <option value="service">Service</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Base Price (₹) *</label>
                    <input
                      type="number"
                      name="pricing.basePrice"
                      className="form-control"
                      value={formData.pricing.basePrice}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>GST (%)</label>
                    <input
                      type="number"
                      name="pricing.gst"
                      className="form-control"
                      value={formData.pricing.gst}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>MOQ</label>
                    <input
                      type="number"
                      name="pricing.moq"
                      className="form-control"
                      value={formData.pricing.moq}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Unit</label>
                    <input
                      type="text"
                      name="pricing.unit"
                      className="form-control"
                      value={formData.pricing.unit}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Stock Quantity</label>
                    <input
                      type="number"
                      name="inventory.quantity"
                      className="form-control"
                      value={formData.inventory.quantity}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                {/* Shipping Address Selector */}
                <div className="form-group shipping-address-section">
                  <label>📦 Ship From Address (Optional)</label>
                  {sellerAddresses.length > 0 ? (
                    <select
                      name="shippingAddressId"
                      className="form-control"
                      value={formData.shippingAddressId}
                      onChange={handleInputChange}
                    >
                      <option value="">Select shipping location...</option>
                      {sellerAddresses.map(address => (
                        <option key={address._id} value={address._id}>
                          {address.addressType === 'shop' ? '🏪' : 
                           address.addressType === 'warehouse' ? '🏭' : '🏢'} {address.label} - {address.city}, {address.state}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="no-addresses-warning">
                      <p>⚠️ No shipping addresses found. Add addresses in Account Settings → Address Book.</p>
                    </div>
                  )}
                  <small className="form-hint">
                    Select the location from where this product will be shipped. This helps buyers know the shipping origin.
                  </small>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingProduct ? 'Update' : 'Create'} Product
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Interested Buyers Modal */}
        {showInterestedBuyersModal && selectedProductInterests && (
          <div className="modal-overlay" onClick={() => setShowInterestedBuyersModal(false)}>
            <div className="modal-content interested-buyers-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>👥 Interested Buyers ({selectedProductInterests.interests.length})</h2>
                <button className="close-btn" onClick={() => setShowInterestedBuyersModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <p className="product-title-info">
                  <strong>Product:</strong> {selectedProductInterests.product.title}
                </p>
                {selectedProductInterests.interests.length === 0 ? (
                  <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                    <p style={{ color: '#6b7280', fontSize: '16px' }}>
                      No active interested buyers found. Some buyers may have been removed from the system.
                    </p>
                  </div>
                ) : (
                  <div className="buyers-table-container">
                    <table className="buyers-table">
                      <thead>
                        <tr>
                          <th>Business Name</th>
                          <th>Contact</th>
                          <th>Location</th>
                          <th>Interested On</th>
                          <th>Profile</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedProductInterests.interests.map((interest, index) => (
                          <tr key={index}>
                            <td>
                              <div className="buyer-name">
                                <strong>{interest.buyer.businessName}</strong>
                                <small className="buyer-type">{interest.buyer.userType}</small>
                              </div>
                            </td>
                            <td>
                              <div className="buyer-contact-info">
                                <div><FaPhone /> {interest.buyer.phone}</div>
                                <div><FaEnvelope /> {interest.buyer.email}</div>
                              </div>
                            </td>
                            <td>
                              {interest.buyer.address && (
                                <div className="buyer-location">
                                  <FaMapMarkerAlt /> {interest.buyer.address.city}, {interest.buyer.address.state}
                                </div>
                              )}
                            </td>
                            <td>
                              <small>{new Date(interest.timestamp).toLocaleDateString()}</small>
                            </td>
                            <td>
                              <a 
                                href={`/public-profile/${interest.buyer._id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-view-profile"
                              >
                                <FaUser /> View Profile
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Products;
